import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useSnackbar } from "notistack";
import { useApprovalActions, useApprovalDetail } from "../../hooks/useApprovals";
import { formatCommentTimestamp, initialsFor } from "../brief/briefUiUtils";
import { canAuthorVersion } from "./approvalKinds";
import {
  ACTOR_COLORS,
  ApprovalPills,
  CreatorCell,
  LinkifiedText,
  PARTY_LABELS,
  StatusChip,
  errorMessage,
  primaryButtonSx,
  secondaryButtonSx,
} from "./approvalUi";

// The one item view every audience uses, for both kinds (script / video) —
// the team's drawer on the board, and the brand's / creator's public
// share-link page. `viewer` is "team", "brand" or "creator"; what each can do
// follows the backend's rules (helpers/approvalWorkflow.js): a version author
// (kind.versionAuthors) saves new versions, anyone remarks; only the brand
// approves or requests changes (the team can record that on the brand's
// behalf); once the brand approves, the share links go read-only until the
// team saves a new version. What a version looks like is kind.View/kind.Editor.

const ACTIVITY_TEXT = {
  version: (a) => `saved version ${a.version}`,
  approved: (a) => (a.onBehalfOf ? `marked v${a.version} approved for the ${PARTY_LABELS[a.onBehalfOf].toLowerCase()}` : `approved v${a.version}`),
  changes_requested: (a) =>
    a.onBehalfOf ? `recorded changes requested by the ${PARTY_LABELS[a.onBehalfOf].toLowerCase()} on v${a.version}` : `requested changes on v${a.version}`,
  remark: () => null,
};

const ActivityItem = ({ item }) => {
  const action = ACTIVITY_TEXT[item.kind](item);
  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700, bgcolor: ACTOR_COLORS[item.author.type], flexShrink: 0 }}>
        {initialsFor(item.author.name.replace(/^@/, ""))}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{item.author.name}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: ACTOR_COLORS[item.author.type] }}>
            {PARTY_LABELS[item.author.type]}
          </Typography>
          {action && <Typography sx={{ fontSize: 13, color: "#6B7280" }}>{action}</Typography>}
          <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>{formatCommentTimestamp(item.createdOn)}</Typography>
        </Box>
        {item.body && (
          <Typography
            sx={{
              fontSize: 13.5,
              color: "#374151",
              mt: 0.25,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              ...(item.kind === "changes_requested" && { borderLeft: "3px solid #F59E0B", pl: 1 }),
            }}
          >
            <LinkifiedText text={item.body} />
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Shared by "Request changes" (brand) and "Record decision" (team, always on
// the brand's behalf). The note is required for changes — the backend rejects
// a change request without one.
const DecisionDialog = ({ open, onClose, viewer, version, isSaving, onSubmit }) => {
  const isTeam = viewer === "team";
  const [decision, setDecision] = useState("changes_requested");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setDecision("changes_requested");
      setNote("");
    }
  }, [open]);

  const needsNote = decision === "changes_requested" && !note.trim();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{isTeam ? `Record a decision on v${version}` : `Request changes on v${version}`}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {isTeam && (
          <ToggleButtonGroup exclusive size="small" value={decision} onChange={(_, v) => v && setDecision(v)}>
            <ToggleButton value="approved" sx={{ textTransform: "none", px: 2 }}>Approved</ToggleButton>
            <ToggleButton value="changes_requested" sx={{ textTransform: "none", px: 2 }}>Changes requested</ToggleButton>
          </ToggleButtonGroup>
        )}
        <TextField
          multiline
          minRows={4}
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 2000 }}
          placeholder={decision === "changes_requested" ? "What should change? (required)" : "Add a note (optional)"}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={needsNote || isSaving}
          onClick={() => onSubmit({ ...(isTeam && { party: "brand" }), decision, version, note: note.trim() })}
          sx={primaryButtonSx}
        >
          {isSaving ? <CircularProgress size={18} color="inherit" /> : isTeam ? "Save decision" : "Send to team"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// `onUploadingChange` (optional): told while a video upload is running, so the
// board's drawer can ask before closing.
const ApprovalReviewPanel = ({ scope, itemId, viewer, onUploadingChange }) => {
  const { kind } = scope;
  const { enqueueSnackbar } = useSnackbar();
  const { data: item, isLoading, error } = useApprovalDetail(scope, itemId);
  const { saveVersion, decide, addRemark } = useApprovalActions(scope, itemId);

  const [editing, setEditing] = useState(false);
  const [viewedVersion, setViewedVersion] = useState(null);
  const [remark, setRemark] = useState("");
  const [decisionOpen, setDecisionOpen] = useState(false);

  useEffect(() => {
    setEditing(false);
    setViewedVersion(null);
    setRemark("");
  }, [itemId]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#FF5200" }} />
      </Box>
    );
  }
  if (error || !item) {
    return <Typography sx={{ p: 3, color: "#B91C1C" }}>{errorMessage(error, `Couldn't load this ${kind.noun}.`)}</Typography>;
  }

  const isTeam = viewer === "team";
  const lockedForParty = !isTeam && item.status === "approved";
  const canEdit = canAuthorVersion(kind, viewer) && !lockedForParty;
  const current = item.currentVersion;
  const shown = item.versions.find((v) => v.number === (viewedVersion ?? current));
  const myDecision = !isTeam ? item.approvals[viewer]?.decision : null;

  const run = (mutation, payload, success, after) =>
    mutation.mutate(payload, {
      onSuccess: () => {
        if (success) enqueueSnackbar(success, { variant: "success" });
        after?.();
      },
      onError: (e) => enqueueSnackbar(errorMessage(e), { variant: "error" }),
    });

  const handleSave = (payload) =>
    run(saveVersion, payload, `Saved as version ${payload.baseVersion + 1}`, () => {
      setEditing(false);
      setViewedVersion(null);
    });

  const handleDecision = (payload) =>
    run(decide, payload, payload.decision === "approved" ? "Approved" : "Change request sent", () => setDecisionOpen(false));

  const handleRemark = () => run(addRemark, { body: remark.trim() }, null, () => setRemark(""));

  const { View, Editor } = kind;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Who + where it stands */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <CreatorCell item={item} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <ApprovalPills approvals={item.approvals} />
          <StatusChip status={item.status} />
        </Box>
      </Box>

      {/* The version itself */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{kind.Noun}</Typography>
            {current > 0 && !editing && (
              <Select
                size="small"
                value={viewedVersion ?? current}
                onChange={(e) => setViewedVersion(e.target.value === current ? null : e.target.value)}
                sx={{ fontSize: 13, height: 30, borderRadius: "8px" }}
              >
                {item.versions.map((v) => (
                  <MenuItem key={v.number} value={v.number} sx={{ fontSize: 13 }}>
                    v{v.number}{v.number === current ? " (latest)" : ""} · {v.author.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
          {current > 0 && !editing && canEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlinedIcon fontSize="small" />}
              onClick={() => setEditing(true)}
              sx={secondaryButtonSx}
            >
              {kind.copy.edit}
            </Button>
          )}
        </Box>

        {editing ? (
          <Editor
            item={item}
            scope={scope}
            isSaving={saveVersion.isLoading}
            onCancel={() => setEditing(false)}
            onSave={handleSave}
            onUploadingChange={onUploadingChange}
          />
        ) : shown ? (
          <Box sx={{ border: "1px solid #EEF0F3", bgcolor: "#FAFAFA", borderRadius: "12px", p: 2 }}>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
              v{shown.number} by {shown.author.name} ({PARTY_LABELS[shown.author.type]}) · {formatCommentTimestamp(shown.createdOn)}
              {shown.number !== current && " · older version"}
            </Typography>
            <View version={shown} versions={item.versions} />
          </Box>
        ) : (
          <Box sx={{ border: "1px dashed #E5E7EB", borderRadius: "12px", p: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13.5, color: "#6B7280", mb: canEdit ? 1.5 : 0 }}>{kind.copy.empty}</Typography>
            {canEdit && (
              <Button variant="contained" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={() => setEditing(true)} sx={primaryButtonSx}>
                {kind.copy.create}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Decisions — the creator doesn't sign off, so their link only sees the final state. */}
      {current > 0 && !editing && (viewer !== "creator" || lockedForParty) && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {lockedForParty ? (
            <Typography sx={{ fontSize: 13, color: "#065F46", fontWeight: 600 }}>
              {(viewer === "creator" && kind.copy.approvedForCreator) ||
                "Approved by the brand. Ask the team if anything needs to change."}
            </Typography>
          ) : isTeam ? (
            <Button variant="outlined" onClick={() => setDecisionOpen(true)} sx={secondaryButtonSx}>
              Record brand decision
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<CheckRoundedIcon />}
                disabled={myDecision === "approved" || decide.isLoading}
                onClick={() => handleDecision({ decision: "approved", version: current })}
                sx={primaryButtonSx}
              >
                {myDecision === "approved" ? `You approved v${current}` : `Approve v${current}`}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ReplayRoundedIcon />}
                onClick={() => setDecisionOpen(true)}
                sx={secondaryButtonSx}
              >
                Request changes
              </Button>
            </>
          )}
        </Box>
      )}

      <Divider />

      {/* One shared thread — team, brand and creator all see every entry. */}
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Remarks & activity</Typography>
        {item.activity.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "#9CA3AF", fontStyle: "italic", mb: 1.5 }}>Nothing yet.</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, mb: 2 }}>
            {item.activity.map((entry) => (
              <ActivityItem key={entry.id} item={entry} />
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            size="small"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            placeholder={`Add a remark — everyone on this ${kind.noun} will see it`}
            sx={{ "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 14 } }}
          />
          <Button
            variant="contained"
            disabled={!remark.trim() || addRemark.isLoading}
            onClick={handleRemark}
            sx={{ ...primaryButtonSx, px: 2, minWidth: 0, height: 40 }}
          >
            {addRemark.isLoading ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon fontSize="small" />}
          </Button>
        </Box>
      </Box>

      <DecisionDialog
        open={decisionOpen}
        onClose={() => setDecisionOpen(false)}
        viewer={viewer}
        version={current}
        isSaving={decide.isLoading}
        onSubmit={handleDecision}
      />
    </Box>
  );
};

export default ApprovalReviewPanel;
